import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

export type UnauthorizedReason = 'expired' | 'session_ended' | 'deactivated' | 'unknown';

export interface UnauthorizedContext {
  currentPath: string;
  currentPathWithSearch: string;
  reason: UnauthorizedReason;
}

export interface ApiClientOptions {
  baseURL: string;
  withCredentials?: boolean;
  timeout?: number;
  headers?: Record<string, string>;
  getToken?: () => string | null;
  onUnauthorized?: (context: UnauthorizedContext) => void;
  /** Path (relative to baseURL) of the silent-refresh endpoint. */
  refreshPath?: string;
  /** Native only: current refresh token to replay via X-Refresh-Token. Web uses the cookie. */
  getRefreshToken?: () => string | null;
  /** Persist a freshly-issued access token after a successful refresh. */
  setAccessToken?: (token: string) => void;
  /** Native only: persist a rotated refresh token returned by /auth/refresh. */
  setRefreshToken?: (token: string | null) => void;
  /** e.g. 'native' — sent as X-Client-Platform so the backend echoes the refresh token in the body. */
  clientPlatform?: string;
}

/** `code` on the synthetic error for an index.html answer to an API call. */
export const NON_API_RESPONSE = 'NON_API_RESPONSE';

/**
 * True for the SPA shell (frontend/web/index.html) — a string body that is an
 * HTML document containing the React mount point. Deliberately narrow so a
 * legitimate HTML payload (an email-template preview, say) is never caught.
 */
export const isSpaShellResponse = (data: unknown): boolean => {
  if (typeof data !== 'string') return false;
  const head = data.slice(0, 200).trimStart().toLowerCase();
  if (!head.startsWith('<!doctype html') && !head.startsWith('<html')) return false;
  return data.includes('<div id="root">');
};

export const createApiClient = ({
  baseURL,
  withCredentials = false,
  timeout,
  headers,
  getToken = () => localStorage.getItem('token'),
  onUnauthorized,
  refreshPath = '/auth/refresh',
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clientPlatform,
}: ApiClientOptions): AxiosInstance => {
  const api = axios.create({
    baseURL,
    withCredentials,
    timeout,
    headers,
  });

  api.interceptors.request.use(
    (config) => {
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        // Let the browser set multipart boundary automatically for FormData bodies.
        const headers = config.headers as Record<string, string | undefined> | undefined;
        if (headers) {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      }

      const token = getToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Let the backend distinguish native (return refresh token in body) from web (cookie).
      if (clientPlatform) {
        config.headers = config.headers || {};
        config.headers['X-Client-Platform'] = clientPlatform;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ── Silent refresh (single-flight) ─────────────────────────────────────────
  // A separate, interceptor-free axios instance performs the refresh so a 401 on
  // the refresh call itself can't recurse back into this handler.
  const refreshClient = axios.create({ baseURL, withCredentials, timeout });
  let refreshInFlight: Promise<boolean> | null = null;

  const doRefresh = async (): Promise<boolean> => {
    try {
      const reqHeaders: Record<string, string> = {};
      const token = getToken();
      if (token) reqHeaders.Authorization = `Bearer ${token}`;
      if (clientPlatform) reqHeaders['X-Client-Platform'] = clientPlatform;
      const nativeRefresh = getRefreshToken?.();
      if (nativeRefresh) reqHeaders['X-Refresh-Token'] = nativeRefresh;

      const res = await refreshClient.post(refreshPath, {}, { headers: reqHeaders });
      const newToken: string | undefined = res.data?.data?.token;
      if (!newToken) return false;
      setAccessToken?.(newToken);
      const rotatedRefresh: string | undefined = res.data?.data?.refreshToken;
      if (rotatedRefresh) setRefreshToken?.(rotatedRefresh);
      return true;
    } catch {
      return false;
    }
  };

  const refreshOnce = (): Promise<boolean> => {
    if (!refreshInFlight) {
      refreshInFlight = doRefresh().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  };

  let notifiedUnauthorized = false;
  const notifyUnauthorized = (reason: UnauthorizedReason) => {
    if (notifiedUnauthorized) return; // de-dupe: N parallel 401s → one logout
    notifiedUnauthorized = true;
    onUnauthorized?.({
      currentPath: window.location.pathname,
      currentPathWithSearch: window.location.pathname + window.location.search,
      reason,
    });
  };

  api.interceptors.response.use(
    (response) => {
      // An API call answered with the web app's own index.html is never a real
      // success. It happens when the CDN's SPA error rule rewrites an API error
      // (e.g. a 403 SUBSCRIPTION_REQUIRED) into "200 + index.html" (#1386).
      // Left as a resolved response, callers read `data.data` off an HTML
      // string and render blank panels or crash; rejecting sends them down
      // their normal error path instead.
      if (isSpaShellResponse(response.data)) {
        return Promise.reject(
          Object.assign(new Error('Unexpected non-API response'), {
            isAxiosError: true,
            config: response.config,
            response: {
              ...response,
              data: {
                success: false,
                code: NON_API_RESPONSE,
                error: 'The server returned an unexpected response. Please try again.',
              },
            },
          })
        );
      }
      return response;
    },
    async (error) => {
      const status = error.response?.status;
      const code: string | undefined = error.response?.data?.code;
      const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

      if (status === 401) {
        // Expired access token → try one silent refresh, then replay the request.
        if (code === 'TOKEN_EXPIRED' && original && !original._retry && setAccessToken) {
          original._retry = true;
          const refreshed = await refreshOnce();
          if (refreshed) {
            original.headers = original.headers || {};
            (original.headers as Record<string, string>).Authorization = `Bearer ${getToken()}`;
            return api(original);
          }
          // Refresh failed — the session is gone (idled out / revoked).
          notifyUnauthorized('session_ended');
          return Promise.reject(error);
        }

        // Terminal: session ended server-side, or a plain unauthenticated 401.
        if (code === 'SESSION_ENDED') {
          notifyUnauthorized('session_ended');
        } else if (code === 'ACCOUNT_DEACTIVATED') {
          // Distinct from an expired session: the account was switched off by an
          // admin mid-session, so the login page must say so rather than telling
          // the user to just sign in again.
          notifyUnauthorized('deactivated');
        } else if (code !== 'TOKEN_EXPIRED') {
          notifyUnauthorized('unknown');
        } else {
          // TOKEN_EXPIRED but refresh not configured (or already retried).
          notifyUnauthorized('expired');
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
};
